import { createContext, useCallback, useContext, useRef, useState } from 'react';
import Modal from './Modal';

const ConfirmContext = createContext(null);

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used within ConfirmProvider');
  }
  return ctx;
}

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false, opts: {} });
  const resolverRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise(resolve => {
      resolverRef.current = resolve;
      setState({ open: true, opts });
    });
  }, []);

  const close = useCallback(result => {
    setState(s => ({ ...s, open: false }));
    if (resolverRef.current) {
      const r = resolverRef.current;
      resolverRef.current = null;
      r(result);
    }
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state.open && (
        <ConfirmInner
          opts={state.opts}
          onCancel={() => close(false)}
          onConfirm={() => close(true)}
        />
      )}
    </ConfirmContext.Provider>
  );
}

function ConfirmInner({ opts, onCancel, onConfirm }) {
  const {
    title = 'Potwierdź',
    message,
    confirmLabel = 'OK',
    cancelLabel = 'Anuluj',
    danger = false,
  } = opts;

  return (
    <Modal title={title} onClose={onCancel} size="sm">
      <div className="flex flex-col gap-5">
        {message && (
          <p style={{ fontSize: '12px', color: 'var(--ink2)', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
            {message}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center justify-center rounded-btn font-bold transition-all"
            style={{
              background: 'var(--bg4)',
              color: 'var(--ink2)',
              border: '1px solid var(--border)',
              fontSize: '9px',
              fontWeight: 700,
              letterSpacing: '0.04em',
              padding: '6px 14px',
            }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="inline-flex items-center justify-center rounded-btn font-bold transition-all"
            style={
              danger
                ? {
                    background: 'rgba(176,88,72,0.18)',
                    color: 'var(--err2)',
                    border: '1px solid rgba(176,88,72,0.35)',
                    fontSize: '9px',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    padding: '6px 14px',
                  }
                : {
                    background: 'var(--acc)',
                    color: '#fff',
                    border: '1px solid rgba(184,84,40,0.6)',
                    fontSize: '9px',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    padding: '6px 14px',
                  }
            }
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}
